import {
  createBrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import type { EmployeeRole } from "@care-by-ella/contracts";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { AppShell, isPathAllowedForRole } from "./components/AppShell";
import { ArticleEditorRoute } from "./routes/ArticleEditor";
import { ArticlesRoute } from "./routes/Articles";
import { ChatInboxRoute } from "./routes/ChatInbox";
import { MediaLibraryRoute } from "./routes/MediaLibrary";
import { FaqListRoute } from "./routes/FaqList";
import { CoachTemplatesRoute } from "./routes/CoachTemplates";
import { FlowEditorRoute } from "./routes/FlowEditor";
import { FlowsListRoute } from "./routes/FlowsList";
import { ImportHistoryRoute } from "./routes/ImportHistory";
import { InboxRoute } from "./routes/Inbox";
import { TransferQueueRoute } from "./routes/TransferQueue";
import { JourneyEditorRoute } from "./routes/JourneyEditor";
import { JourneysListRoute } from "./routes/JourneysList";
import { JourneyPhaseEditorRoute } from "./routes/JourneyPhaseEditor";
import { JourneyPhasesListRoute } from "./routes/JourneyPhasesList";
import { WeeklyThemesRoute } from "./routes/WeeklyThemes";
import { WellnessRoute } from "./routes/Wellness";
import { IntakeEditorRoute } from "./routes/IntakeEditor";
import { LoginRoute } from "./routes/Login";
import { MeetingsRoute } from "./routes/Meetings";
import { patientMeetingHref } from "./lib/patient-links";
import { NewClinicianRoute } from "./routes/NewClinician";
import { StaffRoute } from "./routes/Staff";
import { AccessReviewRoute } from "./routes/AccessReview";
import { AnnouncementCampaignsRoute } from "./routes/AnnouncementCampaigns";
import { OverviewRoute } from "./routes/Overview";
import { WebAnalyticsRoute } from "./routes/WebAnalytics";
import { OnboardingFunnelRoute } from "./routes/OnboardingFunnel";
import { F01InsightsRoute } from "./routes/F01Insights";
import { OnboardingContactsRoute } from "./routes/OnboardingContacts";
import { AwaitingActivationRoute } from "./routes/AwaitingActivation";
import { LabResultEscalationsRoute } from "./routes/LabResultEscalations";
import { SubscriptionEndRefundsRoute } from "./routes/SubscriptionEndRefunds";
import { SupportInboxRoute } from "./routes/SupportInbox";
import { BookingDiagnosticsRoute } from "./routes/BookingDiagnostics";
import { LabPortalenRoute } from "./routes/LabPortalen";
import { MedicationCatalogueRoute } from "./routes/MedicationCatalogue";
import { HandoffQueueRoute } from "./routes/HandoffQueue";
import { PatientDetailRoute } from "./routes/PatientDetail";
import { CoachPatientDetailRoute } from "./routes/CoachPatientDetail";
import { PatientsRoute } from "./routes/Patients";
import { ScheduleRoute } from "./routes/Schedule";
import { SettingsRoute } from "./routes/Settings";
import { MyProfileRoute } from "./routes/MyProfile";
import { WorkQueueRoute } from "./routes/WorkQueue";
import { LaneQueueRoute } from "./routes/LaneQueue";
import { TodayRoute } from "./routes/Today";
import { ClinicQueuesProvider } from "./lib/clinic-queues";
import { Wordmark } from "./components/Wordmark";
import { UnsavedJournalProvider } from "./components/UnsavedJournalGuard";
import { NewVersionBanner } from "./components/NewVersionBanner";
import {
  CoachAdminReasonPrompt,
  SupportAccessGate,
  SupportViewLayout,
} from "./components/SupportView";

// TES-955 — `/patients/:pid/meetings/:mid` → the patient's "Vårdmöten" tab
// with that meeting open.
function LegacyMeetingRedirect() {
  const { patientId = "", meetingId = "" } = useParams();
  return <Navigate to={patientMeetingHref(patientId, meetingId)} replace />;
}

// TES-489 — where each role lands.
//
// Home is the first thing the role sees every morning, so it has to be that
// role's own work. A coach lands on their queue; an admin on the staff roster —
// the one surface that is theirs alone and the only one that is meaningless to
// the other two. An unknown role gets the admin landing for the same reason its
// nav is the admin's: no patient data sits behind it.
//
// TES-506 — the doctor landed on /inbox, one of the three inboxes the
// walkthrough of 2026-09-04 retired. She now lands on /today: today's meetings
// first, then the most urgent item out of each category. The walkthrough:
// "Prio läkare, måste jag se: upcoming meetings"
// "Läkare borde bara se saker som ligger på honom just idag"
// A dashboard answers that; a queue of everything does not.
function homePathForRole(role: string | undefined): string {
  if (role === "coach") return "/work-queue";
  if (role === "doctor") return "/today";
  // TES-580 — the admin lands on the numbers, not the roster.
  return "/overview";
}

function HomeRedirect() {
  const { claims } = useAuth();
  return <Navigate to={homePathForRole(claims?.role)} replace />;
}

// TES-493 — which patient view mounts, decided before either one renders.
//
// Done here rather than as a branch inside PatientDetailRoute on purpose: that
// component composes panels which each fetch doctor-only data on mount (labs,
// photos, transfer review, home-now preview). A coach rendering it and hiding
// panels would still fire those requests and collect 403s; not mounting it is
// what actually keeps the data away.
function PatientDetailForRole() {
  const { claims } = useAuth();
  // TES-1123 — a coach who also holds the admin grant is asked for a support
  // reason on a patient she has no coaching basis for.
  return claims?.role === "coach" ? (
    <CoachAdminReasonPrompt>
      <CoachPatientDetailRoute />
    </CoachAdminReasonPrompt>
  ) : (
    <PatientDetailRoute />
  );
}

// TES-1040 — a patient page in the admin's support view: the doctor's or the
// coach's page, mounted only once today's support reason for this patient
// exists. Keyed on the patient so the next one is checked afresh.
function SupportPatientRoute({ view }: { view: "doctor" | "coach" }) {
  const { id = "" } = useParams();
  return (
    <SupportAccessGate key={id} patientId={id}>
      {view === "coach" ? <CoachPatientDetailRoute /> : <PatientDetailRoute />}
    </SupportAccessGate>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "rehydrating") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-md">
        <Wordmark size="lg" />
      </div>
    );
  }
  if (status === "signed_out") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

// TES-363 — the inline "no access" state (also what routes render when the
// API answers 403/ForbiddenError). Deliberately calm: a coach tapping a
// doctor-only link keeps their session and can navigate on.
function NoAccess() {
  return (
    <div className="flex flex-col gap-md items-start">
      <h1 className="font-display text-3xl tracking-tight">
        Du saknar behörighet
      </h1>
      <p className="text-sm text-textSecondary max-w-md">
        Du saknar behörighet för den här sidan. Du är fortfarande inloggad — gå
        tillbaka till din startsida.
      </p>
      <Link to="/" className="text-sm text-brand hover:underline">
        ← Till startsidan
      </Link>
    </div>
  );
}

// TES-363 — client-side route guard mirroring AppShell's nav role filter:
// a direct URL to an out-of-role route renders the no-access state without
// fetching anything (the API's RolesGuard remains the real gate).
function RoleGate() {
  const { claims } = useAuth();
  const location = useLocation();
  const role = claims?.role as EmployeeRole | undefined;
  if (
    !isPathAllowedForRole(
      role,
      location.pathname,
      claims?.roles,
      claims?.permissions,
    )
  ) {
    return <NoAccess />;
  }
  return <Outlet />;
}

function RedirectIfAuthed({ children }: { children: React.ReactElement }) {
  const { status, claims } = useAuth();
  if (status === "signed_in") {
    return <Navigate to={homePathForRole(claims?.role)} replace />;
  }
  return children;
}

// TES-978 — the unsaved-journal guard needs useBlocker, and useBlocker only
// works under a data router. The route tree stays declarative: one splat
// route renders the same <Routes> the app always had, which is react-router's
// own migration path from <BrowserRouter> (no loaders or actions involved).
function AppRoutes() {
  return (
    <UnsavedJournalProvider>
      {/* TES-1128 — under the router so it can ask on every route change. */}
      <NewVersionBanner />
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <LoginRoute />
            </RedirectIfAuthed>
          }
        />
        <Route
          element={
            <RequireAuth>
              {/* TES-506 — one fetch of "what is waiting", shared by the
                    sidebar badges and the Idag dashboard so they can never
                    disagree. */}
              <ClinicQueuesProvider>
                <AppShell />
              </ClinicQueuesProvider>
            </RequireAuth>
          }
        >
          {/* TES-363 — pathless layout: every shell route renders through
                the role gate, so out-of-role URLs get the no-access state. */}
          <Route element={<RoleGate />}>
            <Route path="/" element={<HomeRedirect />} />
            {/* The coach's stacked workspace. TES-506 split the DOCTOR's
                  copy of this into one route per lane; the coach's four lanes
                  still fit on one screen. */}
            <Route path="/work-queue" element={<WorkQueueRoute />} />
            <Route path="/today" element={<TodayRoute />} />
            {/* TES-506 — a category per menu entry. Five of the six doctor
                  lanes render through LaneQueueRoute; `onboarding` keeps its
                  purpose-built table (the TES-458 resa-filter lives there) and
                  only loses the name "Inkorg". */}
            <Route path="/health-declarations" element={<InboxRoute />} />
            <Route path="/receptovertag" element={<TransferQueueRoute />} />
            <Route
              path="/renewals"
              element={<LaneQueueRoute lane="renewals" />}
            />
            <Route
              path="/side-effects"
              element={<LaneQueueRoute lane="side_effects" />}
            />
            <Route
              path="/medical-reviews"
              element={<LaneQueueRoute lane="medical_reviews" />}
            />
            <Route
              path="/medical-alerts"
              element={<LaneQueueRoute lane="medical_alerts" />}
            />
            {/* TES-519 — the two lanes that replaced the review queue. */}
            <Route
              path="/checkins"
              element={<LaneQueueRoute lane="checkins" />}
            />
            <Route path="/labs" element={<LaneQueueRoute lane="labs" />} />
            {/* TES-765 shipped the lane and the button but never the page
                  they live on, so the card on Idag linked into the role
                  gate's no-access state and the confirmation could not be
                  given at all (TES-787). */}
            {/* TES-915 — due "Följ upp senare" reminders. */}
            <Route
              path="/follow-ups"
              element={<LaneQueueRoute lane="follow_ups" />}
            />
            <Route
              path="/webdoc-confirmations"
              element={<LaneQueueRoute lane="webdoc_confirmations" />}
            />
            <Route
              path="/meetings-to-document"
              element={<LaneQueueRoute lane="meetings_to_document" />}
            />
            <Route
              path="/completion-rounds"
              element={<LaneQueueRoute lane="completion_rounds" />}
            />
            <Route
              path="/doctor-question-answers"
              element={<LaneQueueRoute lane="doctor_question_answers" />}
            />
            {/* Bookmarks from before the split. */}
            <Route
              path="/inbox"
              element={<Navigate to="/health-declarations" replace />}
            />
            <Route path="/meetings" element={<MeetingsRoute />} />
            {/* TES-955 — the meeting moved into the patient's "Vårdmöten"
                  tab. Bookmarks and the doctor's calendar invitation
                  (notifications.service notifyEmployeeMeetingScheduled) still
                  carry the old path. */}
            <Route
              path="/patients/:patientId/meetings/:meetingId"
              element={<LegacyMeetingRedirect />}
            />
            <Route path="/patients" element={<PatientsRoute />} />
            <Route path="/patients/:id" element={<PatientDetailForRole />} />
            {/* TES-491 — the admin's own surface. */}
            <Route path="/overview" element={<OverviewRoute />} />
            <Route path="/web-analytics" element={<WebAnalyticsRoute />} />
            <Route
              path="/onboarding-funnel"
              element={<OnboardingFunnelRoute />}
            />
            {/* TES-1326 — F-01 answers in groups, aggregates only. */}
            <Route path="/f01-insights" element={<F01InsightsRoute />} />
            {/* TES-1109 — the contact list under the funnel. */}
            <Route
              path="/onboarding-contacts"
              element={<OnboardingContactsRoute />}
            />
            {/* TES-1676 — "Utskick", the admin's announcement campaigns. */}
            <Route
              path="/announcements"
              element={<AnnouncementCampaignsRoute />}
            />
            {/* TES-1150 — "Recept utfärdat – inväntar abonnemangsaktivering". */}
            <Route
              path="/awaiting-activation"
              element={<AwaitingActivationRoute />}
            />
            {/* TES-1401 (Ö-473) — "Saknade provsvar", the operational queue. */}
            <Route
              path="/missing-lab-results"
              element={<LabResultEscalationsRoute />}
            />
            {/* TES-1554 (Ö-863) — "Avslut och återbetalning". */}
            <Route
              path="/subscription-end-refunds"
              element={<SubscriptionEndRefundsRoute />}
            />
            <Route
              path="/booking-diagnostics"
              element={<BookingDiagnosticsRoute />}
            />
            {/* TES-1102 — the two-way Ella Support thread. */}
            <Route path="/support-inbox" element={<SupportInboxRoute />} />
            <Route
              path="/support-inbox/:patientId"
              element={<SupportInboxRoute />}
            />
            <Route path="/access-review" element={<AccessReviewRoute />} />
            {/* TES-442 — the doctor's "Provsvar utan koppling", the admin's
                  read-only "LabPortalen" under Drift. */}
            <Route path="/lab-quarantine" element={<LabPortalenRoute />} />
            {/* TES-1292 — the medication catalogue (doctor and admin). */}
            <Route
              path="/medication-catalogue"
              element={<MedicationCatalogueRoute />}
            />
            <Route path="/staff" element={<StaffRoute />} />
            <Route path="/schedule" element={<ScheduleRoute />} />
            <Route path="/articles" element={<ArticlesRoute />} />
            <Route path="/articles/new" element={<ArticleEditorRoute />} />
            <Route path="/articles/:key" element={<ArticleEditorRoute />} />
            <Route path="/flows" element={<FlowsListRoute />} />
            <Route path="/flows/new" element={<FlowEditorRoute />} />
            <Route path="/flows/:key" element={<FlowEditorRoute />} />
            <Route path="/import-history" element={<ImportHistoryRoute />} />
            <Route path="/chat" element={<ChatInboxRoute />} />
            <Route path="/media" element={<MediaLibraryRoute />} />
            <Route path="/handoffs" element={<HandoffQueueRoute />} />
            <Route path="/faq" element={<FaqListRoute />} />
            {/* TES-1355 — the coach welcome template (admin). */}
            <Route path="/coach-templates" element={<CoachTemplatesRoute />} />
            <Route path="/journeys" element={<JourneysListRoute />} />
            <Route path="/journeys/new" element={<JourneyEditorRoute />} />
            <Route path="/journeys/:key" element={<JourneyEditorRoute />} />
            <Route
              path="/journey-phases"
              element={<JourneyPhasesListRoute />}
            />
            <Route
              path="/journey-phases/new"
              element={<JourneyPhaseEditorRoute />}
            />
            <Route
              path="/journey-phases/:key"
              element={<JourneyPhaseEditorRoute />}
            />
            <Route path="/wellness" element={<WellnessRoute />} />
            <Route path="/weekly-themes" element={<WeeklyThemesRoute />} />
            <Route path="/intake-template" element={<IntakeEditorRoute />} />
            <Route
              path="/weekly-checkin-template"
              element={
                <IntakeEditorRoute
                  templateKey="weekly_checkin"
                  heading="Veckans check-in"
                  intro="Redigera frågorna i veckans check-in som patienten fyller i varje vecka. Ändringar sparas som ett utkast och börjar gälla först när du publicerar."
                />
              }
            />
            {/* TES-371: milestone is an editable template kind. */}
            <Route
              path="/milestone-template"
              element={
                <IntakeEditorRoute
                  templateKey="milestone"
                  heading="Månadsavstämning"
                  intro="Redigera frågorna i månadsavstämningen (månad 1, 3, 6, 9 och 12). Fältet 'Månad i behandlingen' fylls i automatiskt av appen och bör inte tas bort. Ändringar sparas som ett utkast och börjar gälla först när du publicerar."
                />
              }
            />
            {/* TES-456: F-07 — the evidence for a prescription transfer. */}
            <Route
              path="/prescription-transfer-template"
              element={
                <IntakeEditorRoute
                  templateKey="prescription_transfer"
                  heading="Underlag för receptövertag"
                  intro="Redigera frågorna i underlaget som en patient med pågående behandling fyller i efter hälsodeklarationen. Fälten för blodtryck och vilopuls (bp_systolic, bp_diastolic, resting_pulse) blir en mätning i patientens hälsohistorik och bör inte döpas om eller tas bort. Ändringar sparas som ett utkast och börjar gälla först när du publicerar."
                />
              }
            />
            {/* TES-1040 — the admin's read-only copies of the doctor's and
                  the coach's pages. Same components, mounted under the
                  view's prefix; the layout supplies the read-only context,
                  the banner and the reason dialog. The role gate lets only
                  an admin in (lib/nav-sections.ts). */}
            <Route
              path="/support/doctor"
              element={<SupportViewLayout viewAs="doctor" />}
            >
              <Route path="today" element={<TodayRoute />} />
              <Route path="meetings" element={<MeetingsRoute />} />
              <Route path="health-declarations" element={<InboxRoute />} />
              <Route
                path="inbox"
                element={<Navigate to="../health-declarations" replace />}
              />
              <Route path="receptovertag" element={<TransferQueueRoute />} />
              <Route
                path="renewals"
                element={<LaneQueueRoute lane="renewals" />}
              />
              <Route
                path="side-effects"
                element={<LaneQueueRoute lane="side_effects" />}
              />
              <Route
                path="medical-reviews"
                element={<LaneQueueRoute lane="medical_reviews" />}
              />
              <Route
                path="medical-alerts"
                element={<LaneQueueRoute lane="medical_alerts" />}
              />
              <Route
                path="checkins"
                element={<LaneQueueRoute lane="checkins" />}
              />
              <Route path="labs" element={<LaneQueueRoute lane="labs" />} />
              <Route
                path="follow-ups"
                element={<LaneQueueRoute lane="follow_ups" />}
              />
              <Route
                path="webdoc-confirmations"
                element={<LaneQueueRoute lane="webdoc_confirmations" />}
              />
              <Route
                path="meetings-to-document"
                element={<LaneQueueRoute lane="meetings_to_document" />}
              />
              <Route
                path="completion-rounds"
                element={<LaneQueueRoute lane="completion_rounds" />}
              />
              <Route
                path="doctor-question-answers"
                element={<LaneQueueRoute lane="doctor_question_answers" />}
              />
              <Route path="handoffs" element={<HandoffQueueRoute />} />
              <Route path="patients" element={<PatientsRoute />} />
              <Route
                path="patients/:id"
                element={<SupportPatientRoute view="doctor" />}
              />
            </Route>
            <Route
              path="/support/coach"
              element={<SupportViewLayout viewAs="coach" />}
            >
              <Route path="work-queue" element={<WorkQueueRoute />} />
              <Route
                path="checkins"
                element={<LaneQueueRoute lane="checkins" />}
              />
              <Route path="handoffs" element={<HandoffQueueRoute />} />
              <Route path="patients" element={<PatientsRoute />} />
              <Route
                path="patients/:id"
                element={<SupportPatientRoute view="coach" />}
              />
            </Route>
            <Route path="/clinicians/new" element={<NewClinicianRoute />} />
            <Route path="/settings" element={<SettingsRoute />} />
            <Route path="/profile" element={<MyProfileRoute />} />
          </Route>
        </Route>
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </UnsavedJournalProvider>
  );
}

const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

export function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
